<template>
  <div class="create-work-form forgot" v-if="step === 0">
    <div class="header">
      <h2>Change your password</h2>
      <button class="close-btn" @click="close">×</button>
    </div>
    <div class="content w-full flex flex-col justify-center">
      <div class="w-full">
        <label>New password</label>
        <input
            type="password"
            v-model="newPassword"
            class="create-input"
            @keyup.enter="submit"
            placeholder="New password *"
        />
        <span class="exp">Your password must contain at least 8 characters.</span>
        <div v-if="!$v.newPassword.required && $v.newPassword.$error" class="error-message">
          Password is required
        </div>
        <div v-if="!$v.newPassword.minLength && $v.newPassword.$error" class="error-message">
          Password must be at least 8 characters
        </div>
      </div>

      <div class="w-full">
        <label>Repeat new password</label>
        <input
            type="password"
            v-model="confirmPassword"
            class="create-input"
            placeholder="Confirm password *"
            @keyup.enter="submit"

        />
        <div v-if="!$v.confirmPassword.required && $v.confirmPassword.$error" class="error-message">
          Confirm password is required
        </div>
        <div v-if="!$v.confirmPassword.sameAsPassword && $v.confirmPassword.$error" class="error-message">
          Passwords must match
        </div>
      </div>

    </div>
    <div class="footer">
      <button class="cancel-btn" @click="close">Discard</button>
      <button :disabled="$v.$invalid" class="create-btn" @click="submit">
        Save
      </button>
    </div>

    <div v-if="loading" class="loading-overlay">
      <div class="spinner"></div>
    </div>
  </div>
  <div class="create-work-form" v-else>
    <div class="header-2">
      <h2>
        Your password is updated.
      </h2>
      <button class="close-btn" @click="close">×</button>
    </div>
  </div>
  <!-- Loading overlay -->
</template>

<script setup lang="ts">
import { defineProps, defineEmits, ref, computed, nextTick } from 'vue';
import { useUserStore } from '~/composables/useUserStore';
import useVuelidate from '@vuelidate/core';
import {required, minLength, sameAs} from "@vuelidate/validators";
import {forgotPassword, resetPassword} from "~/api/userApi";
import {useRouter} from "vue-router";
const router = useRouter();

import * as pkg from 'vue-toastification';
const { useToast } = pkg;
const toast = useToast();
const { user } = useUserStore();
const step = ref(0);
const emailError = ref<any>('');
const forgotPasswordError = ref(false);
const newPassword = ref('');
const confirmPassword = ref('');
const emit = defineEmits(['close']);
const props = defineProps<{
  token: string
}>()

const rules = {
  newPassword: { required, minLength: minLength(8) },
  confirmPassword: {
    required,
    sameAsPassword: sameAs(newPassword),
  },
};

const $v = useVuelidate(rules, {newPassword, confirmPassword});
let debounceTimeout: any = null;
const loading = ref(false); // Add loading state

// Function to handle real-time validation with a debounce delay
const onInputChanged = () => {
  $v.value.$touch(); // Trigger validation

  // Check if both password fields are filled and show warnings if needed
  if (newPassword.value && confirmPassword.value) {
    if (newPassword.value.length < 8) {
      toast.warning('Password must be at least 8 characters.');
    } else if (confirmPassword.value !== newPassword.value) {
      toast.warning('Passwords do not match.');
    }
  }
};


const close = () => {
  step.value = 0
  emit('close');
};
const submit = async () => {
  onInputChanged();
  if (step.value === 0) {
    $v.value.$touch();
    if ($v.value.$invalid) {
      return;
    }
    loading.value = true; // Start loading

    try {
      const data = {
        password: newPassword.value,
        confirmPassword: confirmPassword.value
      }
      const result = await resetPassword(props.token, data);
      if (result) {
        forgotPasswordError.value = false;
        console.log('result', result);
        step.value = 1;
        toast.success(result.message);
        setTimeout(() => {
          close();
          router.push('/login')
        }, 3000);
      }
    } catch (error: any) {
      emailError.value = error.response.data.message;
      toast.warning(error.response.data.message);
      setTimeout(() => {
        emailError.value = '';
      }, 5000);
    } finally {
      loading.value = false; // Stop loading after the request is complete
    }
  } else {
    emit('close');
  }
};


</script>

<style scoped>
.cancel-btn {
  background: #FAFAFA;
  color: #b8b8b8;
    border: none;
    min-width: 140px;
    padding: 5px 0;
    border-radius: 25px;

}
.error-message {
  font-size: 8px;
  line-height: 10px;
  padding: 5px 0;
}
.create-input {
  width: 100%;
  border: .5px solid #b8b8b8;
  border-radius: 10px;
  padding: 10px;
  margin-top: 10px;
}
.create-work-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: relative;
  overflow: hidden;
}
.header {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 40px 0 40px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}
.header-2 {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 80px 40px 80px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}
.header-2 h2,
.header h2 {
  font-size: 28px;
  line-height: 36px;
  text-align: center;
}
.close-btn {
  background: none;
  border: none;
  font-size: 32px;
  cursor: pointer;
  position: absolute;
  right: 20px;
  top: 10px;
}
.content {
  display: flex;
  gap: 5px;
  padding: 0 40px 0 40px;
}
.forgot label {
  font-size: 14px;
}
.footer {
  display: flex;
  justify-content: center;
  background: #EDEDED;
  padding: 20px;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  gap: 20px;
}
.create-btn {
  padding: 5px 40px;
}
.create-btn:enabled {
  background-color: #E3FF00;
  cursor: pointer;
  color: black;
}
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
}
.exp {
  font-size: 8px;
  color: #b8b8b8;
}
.spinner {
  border: 4px solid #f3f3f3;
  border-radius: 50%;
  border-top: 4px solid #3498db;
  width: 40px;
  height: 40px;
  animation: spin 2s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>

